// src/components/DataGrid.js — Excel-style table used across the admin
// [marker: data-grid v1]
//
// • Every column: click the title to sort (A→Z, Z→A, off); ▾ opens an Excel-style
//   filter (search + tick the values to show, sort buttons, clear).
// • Drag a column's right edge to resize. "Columns" button: show/hide + reset.
// • Quick search across visible columns, compact rows, CSV export.
// • Optional row checkboxes for bulk actions (selection is passed to the page).
// • Layout (hidden columns, widths, sort, filters) is saved per staff member on
//   the server (/api/ui-prefs) so it follows them to any computer.
// • Only the rows on screen are drawn, so thousands of rows stay fast.
//
// Column definition:
//   { key, label, width?, type?: 'text'|'number'|'money'|'date'|'bool'|'list',
//     get(row)    → raw value used for sorting (default row[key]),
//     text(row)   → display string used for filters/search/CSV (default formatted get),
//     list(row)   → array of strings (type 'list', e.g. tags) for filters,
//     render(row) → optional JSX for the cell,
//     defaultHidden?: true, align?: 'right' }
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import api from '../utils/api';

const ROW_H = 30;
const BLANK = '(Blanks)';

function fmtDate(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
}
function fmtMoney(v) {
  if (v === null || v === undefined || v === '') return '';
  const n = parseFloat(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
function rawOf(col, row) { return col.get ? col.get(row) : row[col.key]; }
function textOf(col, row) {
  if (col.text) { const t = col.text(row); return t === null || t === undefined ? '' : String(t); }
  if (col.type === 'list') return (col.list ? col.list(row) : []).join(', ');
  const v = rawOf(col, row);
  if (v === null || v === undefined) return '';
  if (col.type === 'date') return fmtDate(v);
  if (col.type === 'money') return fmtMoney(v);
  if (col.type === 'bool') return v ? 'Yes' : 'No';
  return String(v);
}
function filterValuesOf(col, row) {
  if (col.type === 'list') {
    const l = (col.list ? col.list(row) : []).filter(Boolean);
    return l.length ? l : [BLANK];
  }
  const t = textOf(col, row).trim();
  return [t === '' ? BLANK : t];
}
function cmp(col, a, b) {
  const va = rawOf(col, a), vb = rawOf(col, b);
  const ea = va === null || va === undefined || va === '', eb = vb === null || vb === undefined || vb === '';
  if (ea && eb) return 0;
  if (ea) return 1;   // blanks last
  if (eb) return -1;
  if (col.type === 'number' || col.type === 'money') return (parseFloat(va) || 0) - (parseFloat(vb) || 0);
  if (col.type === 'date') return new Date(va).getTime() - new Date(vb).getTime();
  if (col.type === 'bool') return (va ? 1 : 0) - (vb ? 1 : 0);
  return textOf(col, a).localeCompare(textOf(col, b), undefined, { sensitivity: 'base', numeric: true });
}

const EMPTY_PREFS = { hidden: null, widths: {}, sort: null, filters: {} };

export default function DataGrid({
  gridKey, columns, rows, loading, rowKey = 'id',
  onRowClick, selectable = false, selectedIds, onSelectionChange,
  toolbarLeft, toolbarRight, height = 'calc(100vh - 230px)', emptyText = 'No rows match.',
  totals = [],   // [marker: grid-tickets-invoices v1] column keys to sum for the rows shown
  persistFilters = true, // false: filters reset each visit (e.g. one grid shared by every event)
  defaultSort = null,    // [marker: fixes-oct21 v1] { key, dir } used until the person picks a sort
  scrollToEnd = false,   // [marker: fixes-oct21 v1] open scrolled to the last row (like a paper register)
}) {
  const [prefs, setPrefs] = useState(EMPTY_PREFS);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [quick, setQuick] = useState('');
  const [openFilter, setOpenFilter] = useState(null); // { key, x, y } — popup anchored under the ▾
  const [showColumns, setShowColumns] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(600);
  const scrollRef = useRef(null);
  const saveTimer = useRef(null);
  const prefsKey = `grid:${gridKey}`;

  // ── Load saved layout ──
  useEffect(() => {
    let alive = true;
    api.get(`/ui-prefs/${encodeURIComponent(prefsKey)}`)
      .then(r => {
        if (alive && r.data && r.data.value) {
          const v = { ...EMPTY_PREFS, ...r.data.value };
          if (!persistFilters) v.filters = {};
          setPrefs(v);
        }
      })
      .catch(() => {})
      .finally(() => { if (alive) setPrefsLoaded(true); });
    return () => { alive = false; };
  }, [prefsKey]);

  const updatePrefs = useCallback((fn) => {
    setPrefs(prev => {
      const next = fn(prev);
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        const toSave = persistFilters ? next : { ...next, filters: {} };
        api.put(`/ui-prefs/${encodeURIComponent(prefsKey)}`, { value: toSave }).catch(() => {});
      }, 700);
      return next;
    });
  }, [prefsKey, persistFilters]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  // ── Columns in view ──
  const hiddenSet = useMemo(() => {
    if (Array.isArray(prefs.hidden)) return new Set(prefs.hidden);
    return new Set(columns.filter(c => c.defaultHidden).map(c => c.key));
  }, [prefs.hidden, columns]);
  const visibleCols = columns.filter(c => !hiddenSet.has(c.key));
  const widthOf = (c) => (prefs.widths && prefs.widths[c.key]) || c.width || 150;

  // ── Filter + sort ──
  const filtered = useMemo(() => {
    const f = prefs.filters || {};
    const active = columns.filter(c => Array.isArray(f[c.key]));
    const q = quick.trim().toLowerCase();
    let out = rows || [];
    if (active.length) {
      out = out.filter(r => active.every(c => {
        const allow = new Set(f[c.key]);
        return filterValuesOf(c, r).some(v => allow.has(v));
      }));
    }
    if (q) {
      out = out.filter(r => visibleCols.some(c => textOf(c, r).toLowerCase().includes(q)));
    }
    const s = (prefs.sort && prefs.sort.key) ? prefs.sort : defaultSort;
    if (s && s.key) {
      const col = columns.find(c => c.key === s.key);
      if (col) {
        out = [...out].sort((a, b) => {
          const d = cmp(col, a, b);
          // keep blanks last in both directions
          const va = rawOf(col, a), vb = rawOf(col, b);
          const ea = va === null || va === undefined || va === '', eb = vb === null || vb === undefined || vb === '';
          if (ea !== eb) return d;
          return s.dir === 'desc' ? -d : d;
        });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, prefs.filters, prefs.sort, quick, columns, hiddenSet]);

  // [marker: fixes-oct21 v1] start at the bottom (newest entries) when asked
  const endKey = scrollToEnd ? `${(rows || []).length}:${prefsLoaded}` : '';
  useEffect(() => {
    if (!scrollToEnd) return;
    const el = scrollRef.current;
    if (el) { el.scrollTop = el.scrollHeight; setScrollTop(el.scrollTop); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endKey]);

  // ── Virtual window ──
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setViewH(el.clientHeight || 600);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const start = Math.max(0, Math.floor(scrollTop / ROW_H) - 10);
  const end = Math.min(filtered.length, Math.ceil((scrollTop + viewH) / ROW_H) + 10);
  const slice = filtered.slice(start, end);

  // ── Selection ──
  const selSet = useMemo(() => new Set((selectedIds || []).map(String)), [selectedIds]);
  const allFilteredSelected = selectable && filtered.length > 0 && filtered.every(r => selSet.has(String(r[rowKey])));
  const toggleRow = (id) => {
    if (!onSelectionChange) return;
    const s = new Set(selSet);
    const k = String(id);
    if (s.has(k)) s.delete(k); else s.add(k);
    onSelectionChange(Array.from(s));
  };
  const toggleAll = () => {
    if (!onSelectionChange) return;
    if (allFilteredSelected) {
      const remove = new Set(filtered.map(r => String(r[rowKey])));
      onSelectionChange(Array.from(selSet).filter(k => !remove.has(k)));
    } else {
      const s = new Set(selSet);
      filtered.forEach(r => s.add(String(r[rowKey])));
      onSelectionChange(Array.from(s));
    }
  };

  // ── Sort / resize / columns ──
  const cycleSort = (key) => updatePrefs(p => {
    const s = p.sort;
    if (!s || s.key !== key) return { ...p, sort: { key, dir: 'asc' } };
    if (s.dir === 'asc') return { ...p, sort: { key, dir: 'desc' } };
    return { ...p, sort: null };
  });
  const setSort = (key, dir) => updatePrefs(p => ({ ...p, sort: dir ? { key, dir } : null }));
  const setFilter = (key, values) => updatePrefs(p => {
    const f = { ...(p.filters || {}) };
    if (values === null) delete f[key]; else f[key] = values;
    return { ...p, filters: f };
  });
  const toggleHidden = (key) => updatePrefs(p => {
    const cur = Array.isArray(p.hidden) ? p.hidden : columns.filter(c => c.defaultHidden).map(c => c.key);
    const s = new Set(cur);
    if (s.has(key)) s.delete(key); else s.add(key);
    return { ...p, hidden: Array.from(s) };
  });
  const resetLayout = () => { updatePrefs(() => ({ ...EMPTY_PREFS })); setQuick(''); };
  const clearAllFilters = () => { updatePrefs(p => ({ ...p, filters: {} })); setQuick(''); };

  const startResize = (e, col) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = widthOf(col);
    const onMove = (ev) => {
      const w = Math.max(50, Math.min(800, startW + ev.clientX - startX));
      setPrefs(p => ({ ...p, widths: { ...(p.widths || {}), [col.key]: w } }));
    };
    const onUp = (ev) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const w = Math.max(50, Math.min(800, startW + ev.clientX - startX));
      updatePrefs(p => ({ ...p, widths: { ...(p.widths || {}), [col.key]: w } }));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const exportCsv = () => {
    const esc = (s) => `"${String(s).replace(/"/g, '""')}"`;
    const lines = [visibleCols.map(c => esc(c.label)).join(',')];
    filtered.forEach(r => lines.push(visibleCols.map(c => esc(textOf(c, r))).join(',')));
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${gridKey}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };

  const activeFilterCount = Object.keys(prefs.filters || {}).length;
  const totalW = visibleCols.reduce((s, c) => s + widthOf(c), 0) + (selectable ? 36 : 0);

  return (
    <div className="dg-wrap">
      <div className="dg-toolbar">
        <div className="dg-toolbar-left">
          <input className="form-input dg-quick" placeholder="Quick search…" value={quick} onChange={e => setQuick(e.target.value)} />
          {(activeFilterCount > 0 || quick) && (
            <button className="btn btn-ghost btn-sm" onClick={clearAllFilters}>✕ Clear filters{activeFilterCount ? ` (${activeFilterCount})` : ''}</button>
          )}
          {toolbarLeft}
        </div>
        <div className="dg-toolbar-right">
          {toolbarRight}
          <div style={{ position: 'relative' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowColumns(s => !s)}>☰ Columns</button>
            {showColumns && (
              <>
                <div className="dg-click-away" onClick={() => setShowColumns(false)} />
                <div className="dg-pop dg-pop-right">
                  <div className="dg-pop-title">Show columns</div>
                  <div className="dg-pop-list">
                    {columns.map(c => (
                      <label key={c.key} className="dg-check">
                        <input type="checkbox" checked={!hiddenSet.has(c.key)} onChange={() => toggleHidden(c.key)} />
                        <span>{c.label}</span>
                      </label>
                    ))}
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={resetLayout} style={{ marginTop: 6 }}>↺ Reset layout</button>
                </div>
              </>
            )}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={exportCsv} title="Download what you see as a spreadsheet (CSV)">⬇ Export</button>
        </div>
      </div>

      <div className="dg-scroll" ref={scrollRef} style={{ height }} onScroll={e => setScrollTop(e.currentTarget.scrollTop)}>
        <table className="dg-table" style={{ width: totalW }}>
          <colgroup>
            {selectable && <col style={{ width: 36 }} />}
            {visibleCols.map(c => <col key={c.key} style={{ width: widthOf(c) }} />)}
          </colgroup>
          <thead>
            <tr>
              {selectable && (
                <th className="dg-th dg-sel">
                  <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} title="Select all rows shown" />
                </th>
              )}
              {visibleCols.map(c => {
                const s = prefs.sort && prefs.sort.key === c.key ? prefs.sort.dir : null;
                const filtering = Array.isArray((prefs.filters || {})[c.key]);
                return (
                  <th key={c.key} className="dg-th" style={{ textAlign: c.align || 'left' }}>
                    <div className="dg-th-inner">
                      <span className="dg-th-label" onClick={() => cycleSort(c.key)} title="Click to sort">
                        {c.label}{s === 'asc' ? ' ▲' : s === 'desc' ? ' ▼' : ''}
                      </span>
                      <button className={`dg-filter-btn${filtering ? ' on' : ''}`} title="Filter"
                              onClick={e => {
                                e.stopPropagation();
                                const r = e.currentTarget.getBoundingClientRect();
                                setOpenFilter(openFilter && openFilter.key === c.key ? null
                                  : { key: c.key, x: Math.max(8, Math.min(r.left - 200, window.innerWidth - 300)), y: Math.min(r.bottom + 4, window.innerHeight - 420) });
                              }}>▾</button>
                    </div>
                    <span className="dg-resize" onMouseDown={e => startResize(e, c)} />
                    {openFilter && openFilter.key === c.key && (
                      <FilterPopup
                        col={c} rows={rows || []} current={(prefs.filters || {})[c.key]} pos={openFilter}
                        onSort={(dir) => setSort(c.key, dir)}
                        onApply={(vals) => { setFilter(c.key, vals); setOpenFilter(null); }}
                        onClose={() => setOpenFilter(null)}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading || !prefsLoaded ? (
              <tr><td colSpan={visibleCols.length + (selectable ? 1 : 0)} className="dg-empty"><div className="spinner" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={visibleCols.length + (selectable ? 1 : 0)} className="dg-empty">{emptyText}</td></tr>
            ) : (
              <>
                {start > 0 && <tr style={{ height: start * ROW_H }}><td colSpan={visibleCols.length + (selectable ? 1 : 0)} /></tr>}
                {slice.map(r => {
                  const id = r[rowKey];
                  const sel = selSet.has(String(id));
                  return (
                    <tr key={id} className={`dg-row${sel ? ' sel' : ''}${onRowClick ? ' click' : ''}`} style={{ height: ROW_H }}
                        onClick={() => onRowClick && onRowClick(r)}>
                      {selectable && (
                        <td className="dg-td dg-sel" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={sel} onChange={() => toggleRow(id)} />
                        </td>
                      )}
                      {visibleCols.map(c => (
                        <td key={c.key} className="dg-td" style={{ textAlign: c.align || ((c.type === 'money' || c.type === 'number') ? 'right' : 'left') }}
                            title={textOf(c, r)}>
                          {c.render ? c.render(r) : textOf(c, r)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {end < filtered.length && <tr style={{ height: (filtered.length - end) * ROW_H }}><td colSpan={visibleCols.length + (selectable ? 1 : 0)} /></tr>}
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="dg-status">
        Showing {filtered.length.toLocaleString()} of {(rows || []).length.toLocaleString()}
        {totals.map(k => {
          const col = columns.find(c => c.key === k);
          if (!col) return null;
          const sum = filtered.reduce((s, r) => s + (parseFloat(rawOf(col, r)) || 0), 0);
          return <span key={k}> · {col.label}: <strong>{col.type === 'money' ? fmtMoney(sum) : sum.toLocaleString()}</strong></span>;
        })}
        {selectable && selSet.size > 0 && <> · <strong>{selSet.size.toLocaleString()} selected</strong>
          {' '}<button className="btn btn-ghost btn-sm" style={{ padding: '0 6px' }} onClick={() => onSelectionChange && onSelectionChange([])}>clear</button></>}
      </div>
    </div>
  );
}

function FilterPopup({ col, rows, current, pos, onSort, onApply, onClose }) {
  const [search, setSearch] = useState('');
  // Distinct values with counts
  const values = useMemo(() => {
    const m = new Map();
    rows.forEach(r => filterValuesOf(col, r).forEach(v => m.set(v, (m.get(v) || 0) + 1)));
    const arr = Array.from(m.entries()).map(([v, n]) => ({ v, n }));
    const sample = rows.find(r => { const x = rawOf(col, r); return x !== null && x !== undefined && x !== ''; });
    arr.sort((a, b) => {
      if (a.v === BLANK) return 1;
      if (b.v === BLANK) return -1;
      if (sample && (col.type === 'number' || col.type === 'money')) return (parseFloat(String(a.v).replace(/[$,]/g, '')) || 0) - (parseFloat(String(b.v).replace(/[$,]/g, '')) || 0);
      if (sample && col.type === 'date') return new Date(a.v) - new Date(b.v);
      return a.v.localeCompare(b.v, undefined, { sensitivity: 'base', numeric: true });
    });
    return arr;
  }, [col, rows]);
  const [checked, setChecked] = useState(() => new Set(Array.isArray(current) ? current : values.map(x => x.v)));
  const shown = search ? values.filter(x => x.v.toLowerCase().includes(search.toLowerCase())) : values;
  const LIMIT = 500;

  const toggle = (v) => setChecked(s => { const n = new Set(s); if (n.has(v)) n.delete(v); else n.add(v); return n; });
  const allShownChecked = shown.length > 0 && shown.every(x => checked.has(x.v));
  const toggleShown = () => setChecked(s => {
    const n = new Set(s);
    if (allShownChecked) shown.forEach(x => n.delete(x.v)); else shown.forEach(x => n.add(x.v));
    return n;
  });
  const apply = () => {
    // With a search typed, Excel keeps only the matching ticked values
    let vals = Array.from(checked);
    if (search) { const ok = new Set(shown.map(x => x.v)); vals = vals.filter(v => ok.has(v)); }
    if (!search && vals.length === values.length) onApply(null); else onApply(vals);
  };

  return (
    <>
      <div className="dg-click-away" onClick={onClose} />
      <div className="dg-pop dg-pop-fixed" style={{ left: pos.x, top: pos.y }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => { onSort('asc'); onClose(); }}>↑ Sort A→Z</button>
          <button className="btn btn-ghost btn-sm" onClick={() => { onSort('desc'); onClose(); }}>↓ Sort Z→A</button>
        </div>
        <input className="form-input" autoFocus placeholder="Search values…" value={search} onChange={e => setSearch(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter') apply(); if (e.key === 'Escape') onClose(); }} style={{ height: 30, fontSize: 13 }} />
        <div className="dg-pop-list">
          <label className="dg-check" style={{ fontWeight: 600 }}>
            <input type="checkbox" checked={allShownChecked} onChange={toggleShown} />
            <span>(Select all{search ? ' shown' : ''})</span>
          </label>
          {shown.slice(0, LIMIT).map(x => (
            <label key={x.v} className="dg-check">
              <input type="checkbox" checked={checked.has(x.v)} onChange={() => toggle(x.v)} />
              <span className="dg-check-text">{x.v}</span>
              <span className="dg-count">{x.n}</span>
            </label>
          ))}
          {shown.length > LIMIT && <div className="dg-more">…{(shown.length - LIMIT).toLocaleString()} more — type to narrow</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => onApply(null)}>Clear filter</button>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={apply}>OK</button>
          </div>
        </div>
      </div>
    </>
  );
}
